import type { PortfolioState, PortfolioStatus, RiskLevel } from './types';

export const PORTFOLIO_STATUSES = [
  '正常跟踪',
  '重点关注',
  '风险预警',
  '恢复观察',
  '退出准备',
  '已退出',
] as const satisfies readonly PortfolioStatus[];

export const PORTFOLIO_RISK_LEVELS: RiskLevel[] = [
  '低',
  '中',
  '中高',
  '高',
  '极高',
];
const HIGH_RISK_LEVELS: RiskLevel[] = ['高', '极高'];

export type PortfolioRiskFilter = RiskLevel | '高及以上';

export const parsePortfolioRiskFilter = (
  value: string | null,
): PortfolioRiskFilter | undefined => {
  if (value === '高及以上') return value;
  return PORTFOLIO_RISK_LEVELS.find((level) => level === value);
};

export const matchesPortfolioRiskFilter = (
  riskLevel: RiskLevel,
  filter?: PortfolioRiskFilter,
) =>
  !filter ||
  (filter === '高及以上'
    ? HIGH_RISK_LEVELS.includes(riskLevel)
    : riskLevel === filter);

export const selectPortfolioStatusCounts = (state: PortfolioState) => {
  const counts = Object.fromEntries(
    PORTFOLIO_STATUSES.map((status) => [status, 0]),
  ) as Record<PortfolioStatus, number>;
  state.companies.forEach(({ status }) => {
    counts[status] += 1;
  });
  return counts;
};

export const selectDashboardStats = (state: PortfolioState) => ({
  portfolioCount: state.companies.filter((item) => item.status !== '已退出')
    .length,
  normalCount: state.companies.filter((item) => item.status === '正常跟踪')
    .length,
  riskCount: state.companies.filter((item) =>
    HIGH_RISK_LEVELS.includes(item.riskLevel),
  ).length,
  pendingReports: state.reports.filter((item) => item.status === '草稿').length,
  focusIssues: state.issues.filter(
    (item) => !['已关闭', '已取消'].includes(item.status),
  ).length,
  investmentCost: state.investments.reduce(
    (total, item) => total + item.amount,
    0,
  ),
  bookValue: state.companies.reduce((total, item) => total + item.bookValue, 0),
});

export const selectCompanyFacts = (
  state: PortfolioState,
  companyId: string,
) => ({
  company: state.companies.find((item) => item.id === companyId),
  snapshots: state.snapshots.filter((item) => item.companyId === companyId),
  issues: state.issues.filter((item) => item.companyId === companyId),
  risks: state.risks.filter((item) => item.companyId === companyId),
  reports: state.reports.filter((item) => item.companyId === companyId),
  investments: state.investments.filter((item) => item.companyId === companyId),
  opportunities: state.opportunities.filter(
    (item) => item.companyId === companyId,
  ),
});

export const riskLevelRank = (level: string) =>
  ({ 低: 1, 中: 2, 中高: 3, 高: 4, 极高: 5 })[level] ?? 0;
